import { useEffect, useMemo, useRef } from 'react';

import { themeObjectToString, useThemeObserver } from '@atlaskit/tokens';

import { getSavedThemeLocal } from '@confluence/theming/entry-points/getSavedThemeLocal';
import { useThemingState } from '@confluence/theming/entry-points/useThemingState';

import type { WhiteboardThemingService } from './services/whiteboard-theming-service';
import { whiteboardThemingService } from './services/whiteboard-theming-service';

export interface UseThemingService {
	themingService: WhiteboardThemingService;
	initialThemeSerialized?: string;
}

export const useThemingService = (): UseThemingService => {
	const appliedTheme = useThemeObserver();
	const [{ isInitialized: themingInitialized }] = useThemingState();

	const effectiveThemeSerialized = useMemo(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR);
		const theme = isSSR || !themingInitialized ? getSavedThemeLocal() : appliedTheme;
		return theme ? themeObjectToString(theme) : undefined;
	}, [themingInitialized, appliedTheme]);

	// Value should always reflect the originally calculated value
	const initialThemeSerialized = useRef(effectiveThemeSerialized);

	useEffect(() => {
		return () => {
			whiteboardThemingService.dispose();
		};
	}, []);

	useEffect(() => {
		if (appliedTheme) {
			whiteboardThemingService.updateTheme(appliedTheme);
		}
	}, [appliedTheme]);

	return {
		themingService: whiteboardThemingService,
		initialThemeSerialized: initialThemeSerialized.current,
	};
};
